import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { useTabContext } from '@/contexts/TabContext';
import { useGlobalFolderContext } from '@/providers/GlobalFolderProvider';
import { useFolderStructureVisibility, FolderStructureType } from '@/contexts/FolderStructureVisibilityContext';

// Define routes that should open as tabs
const TABBABLE_ROUTES = [
  { path: '/documents', title: 'Documents' },
  { path: '/library', title: 'Library' },
  { path: '/templates', title: 'Templates' },
  { path: '/dialogs', title: 'Dialogs' },
  { path: '/tms', title: 'Localization' },
  { path: '/home', title: 'Home' },
  { path: '/settings', title: 'Settings' },
  { path: '/types-and-tags', title: 'Types and Tags' },
  { path: '/text-management', title: 'Text Management' },
  { path: '/images', title: 'Images' },
  { path: '/ui', title: 'UI Playground' },
  { path: '/relationship', title: 'Relationship' },
  { path: '/logic', title: 'Logic' },
  { path: '/variables', title: 'Variables' },
];

// Map paths to folder structure types for resetting folder selection
const PATH_TO_FOLDER_TYPE: Record<string, FolderStructureType> = {
  '/library': 'library',
  '/documents': 'documents',
  '/templates': 'blueprints',
  '/dialogs': 'dialogs',
  '/variables': 'variables',
  '/logic': 'flows',
};

// Routes that support in-tab navigation (navigating from list to detail stays in same tab)
// Maps base route to a stable tab ID
const IN_TAB_NAVIGATION_CONFIG: Record<string, string> = {
  '/documents': 'tab-documents',
  '/library': 'tab-library',
  '/templates': 'tab-templates',
  '/dialogs': 'tab-dialogs',
  '/logic': 'tab-logic',
  '/variables': 'tab-variables',
};

// Get the base route for a given path
const getBaseRoute = (path: string): string | null => {
  for (const baseRoute of Object.keys(IN_TAB_NAVIGATION_CONFIG)) {
    if (path.startsWith(baseRoute)) {
      return baseRoute;
    }
  }
  return null;
};

// Get the stable tab ID for a path (if it supports in-tab navigation)
const getStableTabId = (path: string): string | null => {
  const baseRoute = getBaseRoute(path);
  return baseRoute ? IN_TAB_NAVIGATION_CONFIG[baseRoute] : null;
};

export const useAutoTabs = () => {
  const location = useLocation();
  const { openTab, tabs, activeTabId, navigateInTab, isHistoryNavigation } = useTabContext();
  const { selectedFolderId } = useGlobalFolderContext();
  const { activeFolderType, setSelectedFolderForType } = useFolderStructureVisibility();
  
  // Track the previous path to detect navigation direction
  const prevPathRef = useRef<string | null>(null);

  useEffect(() => {
    const currentPath = location.pathname;
    const prevPath = prevPathRef.current;
    
    // Skip if path hasn't changed (prevents re-processing on tab state updates)
    if (prevPath === currentPath) {
      return;
    }
    
    // Skip if we're navigating via history (back/forward buttons)
    // The TabContext already handles the navigation and state update
    if (isHistoryNavigation) {
      prevPathRef.current = currentPath;
      return;
    }
    
    // Update previous path for next render
    prevPathRef.current = currentPath;
    
    // Check if this is a tabbable route
    const route = TABBABLE_ROUTES.find(r => {
      return currentPath.startsWith(r.path);
    });

    if (route) {
      // Check if this path supports in-tab navigation (has a stable tab ID)
      const stableTabId = getStableTabId(currentPath);
      
      // Generate tab title
      let tabTitle = route.title;

      // For detail pages, check if item name was passed via navigation state
      // If not, use "Loading..." - the detail page will update via useTabTitleSync
      const isDetailPath = currentPath !== route.path && currentPath !== `${route.path}/`;
      
      // Special handling for "new item" routes - give them descriptive titles
      if (currentPath.endsWith('/new')) {
        if (currentPath.startsWith('/documents/')) tabTitle = 'New Document';
        else if (currentPath.startsWith('/library/')) tabTitle = 'New Object';
        else if (currentPath.startsWith('/templates/')) tabTitle = 'New Template';
        else if (currentPath.startsWith('/dialogs/')) tabTitle = 'New Dialog';
        else if (currentPath.startsWith('/logic/')) tabTitle = 'New Flow';
        else if (currentPath.startsWith('/variables/')) tabTitle = 'New Variable';
        else tabTitle = 'New Item';
      } else if (isDetailPath && (
        currentPath.startsWith('/documents/') ||
        currentPath.startsWith('/library/') ||
        currentPath.startsWith('/templates/') ||
        currentPath.startsWith('/dialogs/') ||
        currentPath.startsWith('/logic/') ||
        currentPath.startsWith('/variables/')
      )) {
        // Use item name from navigation state if available
        const state = location.state as { itemName?: string } | null;
        tabTitle = state?.itemName || 'Loading...';
      }

      // Determine item type for folder-aware pages
      let itemType: string | undefined;
      if (currentPath.startsWith('/documents')) itemType = 'documents';
      else if (currentPath.startsWith('/library')) itemType = 'library';
      else if (currentPath.startsWith('/templates')) itemType = 'blueprints';
      else if (currentPath.startsWith('/dialogs')) itemType = 'dialogs';
      else if (currentPath.startsWith('/logic')) itemType = 'flows';
      else if (currentPath.startsWith('/variables')) itemType = 'variables';

      // Determine if this is a detail page (e.g., /dialogs/123) vs list page (e.g., /dialogs)
      const isDetailPage = currentPath !== route.path && currentPath !== `${route.path}/`;
      
      // Get the folder type for this path
      const folderType = PATH_TO_FOLDER_TYPE[route.path];
      
      // Reset folder selection when navigating to a list page (not detail page)
      // This ensures users see all items when clicking on a page in the sidebar
      if (!isDetailPage && folderType) {
        setSelectedFolderForType(folderType, null);
      }
      
      // Find the currently active tab - use isActive flag as source of truth, not activeTabId
      // (activeTabId can be out of sync due to React state batching)
      const activeTab = tabs.find(t => t.isActive) || null;
      
      // Check if this is a "new item" route - these should ALWAYS create their own tab
      // so that unsaved changes can be tracked properly per tab
      const isNewItemRoute = currentPath.endsWith('/new');
      
      // Use in-tab navigation if:
      // 1. This is a DETAIL page (not a list/sidebar navigation)
      // 2. This is NOT a "new item" route (new items get their own tab)
      // 3. There is an active tab to navigate within
      // 4. The current path is different from the active tab's path
      //
      // This ensures:
      // - Sidebar clicks (list pages like /templates) always open/activate tabs
      // - Clicking items (detail pages like /templates/123) navigates within current tab
      // - New item routes (/library/new, /templates/new) always get their own tab
      // - Works from ANY page (Home, Library, Templates, etc.)
      const shouldUseInTabNavigation = 
        isDetailPage && // Must be a detail page (clicking an item, not sidebar)
        !isNewItemRoute && // New items always get their own tab
        activeTab && // Must have an active tab to navigate within
        activeTab.path !== currentPath; // Path must be different
      
      if (shouldUseInTabNavigation && activeTab) {
        // Navigate within the CURRENT ACTIVE tab (updates history without creating new tab)
        // This works from any page - Home, Library list, Templates list, etc.
        navigateInTab(currentPath, tabTitle, activeTab.id);
      } else {
        // Check if a tab with this exact PATH already exists (e.g., opened from sidebar)
        // This prevents conflicts when sidebar opens a tab with path-based ID
        const existingTabByPath = tabs.find(t => t.path === currentPath);
        
        if (existingTabByPath) {
          // Tab with this path already exists - just activate it
          // This happens when sidebar opens a specific item tab
          openTab({
            id: existingTabByPath.id,
            title: existingTabByPath.title,
            path: currentPath,
            baseTitle: existingTabByPath.baseTitle,
          }, {
            activate: true
          });
        } else {
          // No tab with this path exists - create/activate using stable ID or path
          // New item routes always use path as ID to ensure they get their own tab
          const tabId = isNewItemRoute ? currentPath : (stableTabId || currentPath);
          
          // Preserve folder ID for detail pages in the same item type
          const shouldPreserveFolderId = isDetailPage && itemType && activeFolderType === itemType;
          const folderIdToUse = shouldPreserveFolderId ? selectedFolderId : null;
          
          // Open the tab - openTab will handle:
          // - Creating new tab if it doesn't exist
          // - Activating existing tab if it does exist (by ID)
          openTab({
            id: tabId,
            title: tabTitle,
            path: currentPath,
            baseTitle: route.title,
            folderInfo: itemType ? {
              folderId: folderIdToUse,
              folderName: null,
              itemType
            } : undefined
          }, {
            activate: true
          });
        }
      }
    }
  }, [location.pathname, location.state, openTab, selectedFolderId, activeFolderType, setSelectedFolderForType, tabs, activeTabId, navigateInTab, isHistoryNavigation]);
};

export default useAutoTabs;
